<template>
    <div>
        <b-card class="detail-card mb-2 compare-info-card" header-class="compare-info-card-header"
            header-text-variant="white">
            <template #header>
                <h4 class="font-weight-bold text-center mb-1 compare-name-text">
                    {{ `${point.name}` }}
                </h4>
                <div class="text-center compare-address-text">
                    {{ `(${point.lat}, ${point.long})` }}
                </div>
            </template>
            <b-row class="no-gutters align-items-center compare-body-row">
                <b-col cols="5" class="text-center">
                    <h1 class="mb-0 font-weight-bold compare-last-value-text" v-if="lastValueText">
                        {{ lastValueText }}
                    </h1>
                </b-col>
                <b-col cols="2" class="text-center">
                    <b-img v-if="legendIconUrl" fluid class="compare-legend-icon" :src="legendIconUrl" />
                </b-col>
                <b-col cols="5" class="text-center">
                    <div class="compare-legend-text" v-if="lastLegendText">
                        {{ lastLegendText }}
                    </div>
                </b-col>
            </b-row>
            <div class="mt-1 text-center compare-update-text" v-if="lastUpdateText">
                Cập nhật lúc: {{ lastUpdateText }}
            </div>
        </b-card>
        <b-card class="detail-card mb-0" header-class="rainValueGather10-card-bg" header-text-variant="white">
            <div class="mb-2">
                Chọn điểm khác để so sánh:
                <v-select :options="listOtherPoint" :clearable="false" :multiple="true" :filterable="false"
                    :searchable="false" label="name" v-model="selectedLayerPoint"></v-select>
            </div>
            <div style="display: flex; justify-content: space-between;">
                <DatePicker v-model="range" range lang="vi" format="DD/MM/YYYY" name="time" :shortcuts="shortcuts">
                </DatePicker>
                <v-select style="width: 240px;" v-model="type" class="ml-2" :options="typeOptions" :clearable="false"
                    :filterable="false" :searchable="false"></v-select>
            </div>
            <b-overlay :show="isLoading" spinner-variant="primary" variant="light" opacity="0.5" blur="1px"
                rounded="sm">
                <b-row class="mt-2">
                    <b-col cols="12" v-for="(item, index) in listMap" :key="item.name">
                        <apex-chart :type="chartType" :height="320" :options="chartOptions(item.name)"
                            :series="chartSeries(index)" />
                    </b-col>
                </b-row>
            </b-overlay>
        </b-card>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment-timezone';
import API_CONFIG from '@/api-config';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';
import { getRange } from '../utils/datetime';

export default {
    components: {
        DatePicker
    },
    async mounted() {
        await this.$nextTick();
        this.typeOptions = await this.getDataType();
        if (this.typeOptions.length === 0) return;
        this.type = this.typeOptions[0];
        this.range = getRange(7);
        this.getData();
    },
    props: {
        point: {
            type: Object,
            required: true
        },
        listLayerPoint: {
            type: Array,
            required: true
        },
    },
    computed: {
        listOtherPoint() {
            return this.listLayerPoint.filter(item => item.id !== this.point.id);
        },
        mainSeries() {
            const series = this.listStatistic?.[0]?.[0]?.data;
            return Array.isArray(series) ? series : [];
        },
        lastValueText() {
            const selectedPointValue = this.point?.data?.value;
            if (selectedPointValue != null && !Number.isNaN(selectedPointValue)) {
                return this.formatValue(selectedPointValue);
            }
            if (this.mainSeries.length === 0) return '';
            const value = this.mainSeries[this.mainSeries.length - 1]?.value;
            return this.formatValue(value);
        },
        lastValueNumber() {
            if (this.mainSeries.length === 0) return null;
            const value = Number(this.mainSeries[this.mainSeries.length - 1]?.value);
            if (Number.isNaN(value)) return null;
            return value;
        },
        legend() {
            if (Array.isArray(this.point?.legend) && this.point.legend.length > 0) return this.point.legend;
            if (Array.isArray(this.point?.layer?.legend) && this.point.layer.legend.length > 0) return this.point.layer.legend;
            return [];
        },
        matchedLegendItem() {
            if (this.legend.length === 0 || this.lastValueNumber == null) return null;
            for (const item of this.legend) {
                if (this.lastValueNumber <= item.value) return item;
            }
            return this.legend[this.legend.length - 1] || null;
        },
        lastLegendText() {
            const item = this.matchedLegendItem;
            if (!item) return '';
            return item.name || '';
        },
        legendIconUrl() {
            const icon = this.matchedLegendItem?.icon;
            if (!icon) return '';
            if (icon.startsWith('http://') || icon.startsWith('https://')) return icon;
            return API_CONFIG.BASE_URL + 'download?fileName=' + icon;
        },
        chartType() {
            return this.point?.chartType === 'bar' ? 'bar' : 'line';
        },
        lastUpdateText() {
            if (this.mainSeries.length === 0) return '';
            const lastItem = this.mainSeries[this.mainSeries.length - 1];
            if (lastItem?.time) return moment(lastItem.time).format('DD/MM/YYYY HH:mm');
            return lastItem?.label || '';
        }
    },
    data: function () {
        return {
            shortcuts: [
                {
                    text: '1 tuần qua',
                    onClick() {
                        return getRange(7);
                    }
                },
                {
                    text: '2 tuần qua',
                    onClick() {
                        return getRange(14);
                    }
                },
                {
                    text: '1 tháng qua',
                    onClick() {
                        return getRange(31);
                    }
                },
                {
                    text: '2 tháng qua',
                    onClick() {
                        return getRange(61);
                    }
                },
                {
                    text: '3 tháng qua',
                    onClick() {
                        return getRange(92);
                    }
                },
                {
                    text: '6 tháng qua',
                    onClick() {
                        return getRange(183);
                    }
                },
                {
                    text: '1 năm qua',
                    onClick() {
                        return getRange(365);
                    }
                }
            ],
            range: [],
            unit: '',
            typeOptions: [],
            type: null,
            isLoading: false,
            listMap: [
                {
                    name: 'Trung bình ngày',
                    value: 'flex'
                }
            ],
            listStatistic: [[]],
            selectedLayerPoint: []
        };
    },
    watch: {
        range() {
            this.getData();
        },
        type(value) {
            this.getData();
        },
        selectedLayerPoint() {
            this.getData();
        }
    },
    methods: {
        ...mapActions({
            getStatisticFlex: 'statistic/flex',
            getDataType: 'data/getDataType'
        }),
        async getData() {
            if (this.isLoading) return;
            this.isLoading = true;
            this.listStatistic = [[]];
            const listPoint = [this.point, ...this.selectedLayerPoint];
            for (const point of listPoint) {
                const listData = await this.getDataPoint(point.id);
                for (let i = 0; i < this.listMap.length; i++) {
                    this.listStatistic[i].push({
                        name: point.name,
                        data: listData[i]
                    });
                }
            }
            this.isLoading = false;
        },
        async getDataPoint(pointId) {
            try {
                const params = { pointId, type: this.type };
                if (this.range.length === 2) {
                    params.from = this.range[0];
                    params.to = this.range[1];
                }
                return await Promise.all([this.getStatisticFlex(params)]);
            } catch (error) { }
        },
        chartSeries(index) {
            const series = this.listStatistic?.[index] || [];
            return series.map(item => ({
                name: item.name,
                data: (item.data || [])
                    .filter(dataItem => dataItem?.value != null)
                    .map(dataItem => ({
                        x: dataItem.label,
                        y: dataItem.value
                    }))
            }));
        },
        chartOptions(title) {
            return {
                chart: {
                    id: `compare-${title}`,
                    toolbar: {
                        tools: {
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            reset: false,
                            pan: false
                        }
                    }
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                dataLabels: {
                    enabled: false
                },
                grid: {
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                title: {
                    text: title,
                    align: 'center'
                },
                xaxis: {
                    labels: {
                        rotate: 0,
                        hideOverlappingLabels: true
                    }
                },
                yaxis: {
                    labels: {
                        formatter: value => this.formatValue(value)
                    }
                },
                tooltip: {
                    y: {
                        formatter: value => this.formatValue(value)
                    }
                }
            };
        },
        formatValue(value) {
            if (value == null || Number.isNaN(value)) return '';
            if (value === 0) return '0';
            if (Math.abs(value) < 0.1) return value.toFixed(2);
            if (Math.abs(value) < 1) return value.toFixed(1);
            return Number(value).toFixed(3);
        }
    }
};
</script>
<style>
.mx-datepicker-sidebar {
    width: 120px;
}

.v-select .vs__selected-options {
    flex-wrap: nowrap;
    white-space: nowrap;
    overflow: hidden;
}

.compare-last-value-text {
    font-size: 3.4rem;
    line-height: 1.1;
}

.compare-body-row {
    min-height: 82px;
}

.compare-legend-icon {
    width: 68px;
    height: 68px;
    object-fit: contain;
}

.compare-legend-text {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
}

.compare-name-text {
    font-size: 1rem;
}

.compare-address-text {
    font-size: 0.875rem;
}

.compare-update-text {
    font-size: 0.8rem;
    opacity: 0.95;
}

.compare-info-card {
    background-color: #4a90d9;
    color: #fff;
}

.compare-info-card .card-header {
    background-color: #3378c0;
    color: #fff;
    border-bottom: 0;
}
</style>
